import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'

import { defaultPaging } from 'clickNgo/configs/crud'
import { IPartnerFlag } from 'clickNgo/entities/partner/models/partnerFlag'
import { ProfilingCodeType } from 'clickNgo/entities/profilingCode'
import { I519Form } from 'clickNgo/models/crud/forms/i519Form'
import { ICrudFetchOptions } from 'clickNgo/models/crud/utils'
import { toServerFormat } from 'core-components/helpers/date'
import { IDataProviderFilter, IDataProviderSort, Logic, Operator } from 'core-components/types'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'

import { mapPartnerFlagFrom519Crud } from './mapping/mapPartnerFlagFrom519Crud'

interface IFetchPartnerFlagsRequestsByIdAndType {
  partnerId: string
  type?: ProfilingCodeType
}

export const fetchPartnerFlagsRequestsByIdAndType = (
  { partnerId, type }: IFetchPartnerFlagsRequestsByIdAndType,
  options?: ICrudFetchOptions
): Promise<IPartnerFlag[]> => {
  const crud = CrudStore.get<I519Form>(CrudStoreItem.PartnerFlagsRequests, options?.cacheKey)
  const sortBy: IDataProviderSort = { field: 'Code', dir: 'asc' }
  const paging = options?.paging || defaultPaging

  const endDate = new Date()
  endDate.setMinutes(0)
  endDate.setSeconds(0)
  endDate.setMilliseconds(0)

  // We should show the flag as active if it was done in the last 48 hours
  const doneStatusModifiedDate = new Date(new Date().getTime() - 1000 * 60 * 60 * 48).toISOString()

  const filterByPartnerId = {
    field: 'PartnerId',
    operator: Operator.EQUAL,
    value: partnerId,
  }

  const filterByType = {
    field: 'Type',
    operator: Operator.EQUAL,
    value: type || '',
  }

  const filterByRejectStatus = {
    field: 'Status',
    operator: Operator.NOTEQUAL,
    value: 'Rejected',
  }

  const filterByNonRemoveAction = {
    field: 'Action',
    operator: Operator.NOTEQUAL,
    value: 'Remove',
  }

  const filterByRemoveAction = {
    field: 'Action',
    operator: Operator.EQUAL,
    value: 'Remove',
  }

  const filterByDate = {
    field: 'EndDate',
    operator: Operator.GTE,
    value: toServerFormat(endDate),
  }

  const filterByStatus: IDataProviderFilter = {
    logic: Logic.AND,
    filters: [
      {
        logic: Logic.OR,
        filters: [
          {
            field: 'Status',
            operator: Operator.NOTEQUAL,
            value: 'Done',
          },
          {
            field: 'Modified',
            operator: Operator.GTE,
            value: doneStatusModifiedDate,
          },
        ],
      },
      filterByRejectStatus,
    ],
  }

  const filters = type
    ? [filterByPartnerId, filterByStatus, filterByType]
    : [filterByPartnerId, filterByStatus]

  const defaultFilter: IDataProviderFilter = {
    logic: Logic.AND,
    filters: [
      {
        logic: Logic.OR,
        filters: [
          {
            logic: Logic.AND,
            filters: [filterByNonRemoveAction, filterByDate, ...filters],
          },
          {
            logic: Logic.AND,
            filters: [filterByRemoveAction, ...filters],
          },
        ],
      },
    ],
  }

  crud.setFilter(defaultFilter)
  crud.setSort(sortBy)
  crud.setPaging(paging)

  if (options?.isForce) {
    crud.removeQueries(true)
  }

  return crud
    .fetch({
      skipHierarchyFilter: options?.skipHierarchyFilter,
      force: options?.isForce,
      skipTotal: options?.skipTotal,
    })
    .then((result: { items: I519Form[] }) => {
      if (result && result.items) {
        return result.items.map(mapPartnerFlagFrom519Crud)
      }

      return []
    })
    .catch((e: string) => {
      Logger.error(e)
      return []
    })
}
